<script setup lang="ts">
import type { MemoryEvent, MemorySummary } from '@/api/memory/memoryType';
import { Icon } from '@iconify/vue';
import axios from 'axios';
import { computed, onUnmounted, ref, watch } from 'vue';
import { getMemoryEvents, getMemorySummary, getMemorySummaryHistory, refreshMemorySummary } from '@/api/memory/memoryApi';
import { error, info } from '@/utils/toast';
import MemoryActivityTimeline from './MemoryActivityTimeline.vue';
import MemorySummaryPanel from './MemorySummaryPanel.vue';

const props = defineProps<{
  active: boolean;
}>();

const pageSize = 20;
const summary = ref<MemorySummary | null>(null);
const legacySummaries = ref<MemorySummary[]>([]);
const events = ref<MemoryEvent[]>([]);
const summaryLoading = ref(false);
const summaryRefreshing = ref(false);
const eventsLoading = ref(false);
const hasLoaded = ref(false);
const currentPage = ref(1);
const totalCount = ref(0);
const search = ref('');
const eventType = ref('');
const includeLegacy = ref(false);
const loadError = ref(false);
let searchTimer: ReturnType<typeof setTimeout> | null = null;
let eventsController: AbortController | null = null;

const totalPages = computed(() => Math.max(1, Math.ceil(totalCount.value / pageSize)));

async function loadSummary() {
  summaryLoading.value = true;
  try {
    summary.value = await getMemorySummary();
  }
  catch (cause) {
    console.error('Failed to load memory summary:', cause);
    loadError.value = true;
  }
  finally {
    summaryLoading.value = false;
  }
}

async function refreshSummary() {
  summaryRefreshing.value = true;
  loadError.value = false;
  try {
    const result = await refreshMemorySummary();
    summary.value = result.summary;
    if (result.status === 'no_changes')
      info('没有新增学习活动，未重新调用模型');
  }
  catch (cause) {
    console.error('Failed to refresh memory summary:', cause);
    if (axios.isAxiosError(cause) && cause.response?.status === 409) {
      info('学习摘要正在生成，请稍后刷新');
    }
    else {
      loadError.value = true;
      error('学习摘要服务暂不可用，请稍后重试');
    }
  }
  finally {
    summaryRefreshing.value = false;
  }
}

async function loadEvents() {
  eventsController?.abort();
  const controller = new AbortController();
  eventsController = controller;
  eventsLoading.value = true;

  try {
    const result = await getMemoryEvents({
      page: currentPage.value,
      page_size: pageSize,
      event_type: eventType.value || undefined,
      search: search.value || undefined,
      include_legacy: includeLegacy.value || undefined,
    }, { signal: controller.signal });
    if (controller.signal.aborted)
      return;
    events.value = result.results;
    totalCount.value = result.count;
  }
  catch (cause) {
    if (controller.signal.aborted)
      return;
    console.error('Failed to load memory events:', cause);
    loadError.value = true;
  }
  finally {
    if (eventsController === controller)
      eventsLoading.value = false;
  }
}

async function toggleLegacyHistory() {
  includeLegacy.value = !includeLegacy.value;
  currentPage.value = 1;
  if (includeLegacy.value) {
    try {
      const history = await getMemorySummaryHistory();
      summary.value = history.summary;
      legacySummaries.value = history.legacy_summaries;
    }
    catch (cause) {
      console.error('Failed to load legacy memory summaries:', cause);
      loadError.value = true;
    }
  }
  await loadEvents();
}

async function refresh() {
  loadError.value = false;
  await Promise.all([loadSummary(), loadEvents()]);
  if (loadError.value)
    error('记忆数据加载失败，请稍后重试');
}

function updateFilters() {
  currentPage.value = 1;
  void loadEvents();
}

function onSearchInput() {
  if (searchTimer)
    clearTimeout(searchTimer);
  searchTimer = setTimeout(updateFilters, 300);
}

function changePage(page: number) {
  currentPage.value = page;
  void loadEvents();
}

watch(
  () => props.active,
  (active) => {
    if (active && !hasLoaded.value) {
      hasLoaded.value = true;
      void refresh();
    }
  },
  { immediate: true },
);

onUnmounted(() => {
  if (searchTimer)
    clearTimeout(searchTimer);
  eventsController?.abort();
});
</script>

<template>
  <div class="space-y-6 pb-1">
    <div class="flex flex-wrap items-start justify-between gap-3">
      <div>
        <h2 class="text-lg font-semibold">
          记忆看板
        </h2>
        <p class="mt-1 text-sm text-muted-foreground">
          查看 AI 助教当前可用的学习背景与活动记录。
        </p>
      </div>
      <button
        type="button"
        title="刷新记忆数据"
        aria-label="刷新记忆数据"
        class="inline-flex size-9 items-center justify-center rounded-md border text-muted-foreground hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50"
        :disabled="summaryLoading || summaryRefreshing || eventsLoading"
        @click="refresh"
      >
        <Icon icon="lucide:refresh-cw" class="size-4" :class="(summaryLoading || summaryRefreshing || eventsLoading) && 'animate-spin'" />
      </button>
    </div>

    <div v-if="loadError" class="flex items-center justify-between gap-3 rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive">
      <span>部分记忆数据暂时无法加载。</span>
      <button type="button" class="shrink-0 underline" @click="refresh">
        重试
      </button>
    </div>

    <div class="flex justify-end">
      <button
        type="button"
        class="inline-flex h-9 items-center gap-2 rounded-md border px-3 text-sm hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50"
        :disabled="summaryLoading || summaryRefreshing"
        @click="refreshSummary"
      >
        <Icon icon="lucide:sparkles" class="size-4" :class="summaryRefreshing && 'animate-spin'" />
        生成最新摘要
      </button>
    </div>

    <MemorySummaryPanel :summary="summary" :loading="summaryLoading || summaryRefreshing" />

    <section class="space-y-4">
      <div class="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h3 class="text-base font-semibold">
            活动记录
          </h3>
          <p class="mt-1 text-sm text-muted-foreground">
            共 {{ totalCount }} 条记录
          </p>
        </div>
        <div class="flex w-full flex-col gap-2 sm:w-auto sm:flex-row">
          <div class="relative min-w-0 sm:w-60">
            <Icon icon="lucide:search" class="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
            <input
              v-model="search"
              type="search"
              class="h-9 w-full rounded-md border bg-background pl-9 pr-3 text-sm outline-none focus-visible:ring-2 focus-visible:ring-ring"
              placeholder="搜索路径或内容"
              @input="onSearchInput"
            >
          </div>
          <select v-model="eventType" class="h-9 rounded-md border bg-background px-3 text-sm outline-none focus-visible:ring-2 focus-visible:ring-ring" @change="updateFilters">
            <option value="">
              全部活动
            </option>
            <option value="chat_message">
              聊天消息
            </option>
            <option value="http_request">
              平台活动
            </option>
          </select>
          <label class="flex h-9 items-center gap-2 whitespace-nowrap px-1 text-sm text-muted-foreground">
            <input
              :checked="includeLegacy"
              type="checkbox"
              class="size-4 rounded border"
              @change="toggleLegacyHistory"
            >
            历史记录
          </label>
        </div>
      </div>

      <details v-if="includeLegacy && legacySummaries.length" class="border-b pb-4">
        <summary class="cursor-pointer text-sm text-muted-foreground">
          归档摘要（{{ legacySummaries.length }}）
        </summary>
        <div class="mt-3 space-y-3">
          <article v-for="legacySummary in legacySummaries" :key="legacySummary.id" class="border-l-2 pl-3 text-sm text-muted-foreground">
            <p class="whitespace-pre-wrap">
              {{ legacySummary.summary }}
            </p>
          </article>
        </div>
      </details>

      <MemoryActivityTimeline
        :events="events"
        :loading="eventsLoading"
        :current-page="currentPage"
        :total-pages="totalPages"
        :total-count="totalCount"
        :search="search"
        :event-type="eventType"
        @page-change="changePage"
        @retry="refresh"
      />
    </section>
  </div>
</template>
